import { DailyStatsType } from '@components/Home/Stats'
import * as lockr from 'lockr'

const DB_NAME = 'hayirHahStatsBackup'
const DB_VERSION = 2
const STATS_STORE_NAME = 'statsBackups'
const LS_BACKUP_KEY = 'stats_backups'
const MAX_BACKUPS = 5

type StatsBackupEntry = {
  id: string // Using string id based on date
  timestamp: number
  stats: DailyStatsType
}

// Utility functions for date handling
const dateUtils = {
  // Get the date key for today (YYYY-MM-DD format)
  getTodayKey(): string {
    const now = new Date()
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(
      now.getDate()
    ).padStart(2, '0')}`
  },

  // Format a readable date from a timestamp
  formatBackupDate(timestamp: number): string {
    return new Date(timestamp).toLocaleString()
  },

  // Creates a deep copy of an object
  deepCopy<T>(obj: T): T {
    return JSON.parse(JSON.stringify(obj))
  },
}

// Check if IndexedDB is supported
const isIndexedDBSupported = (): boolean => {
  return typeof window !== 'undefined' && window.indexedDB !== undefined
}

/**
 * Opens the IndexedDB connection with retry logic and robust error handling
 */
const openDB = (retries = 1): Promise<IDBDatabase> => {
  return new Promise((resolve, reject) => {
    if (!isIndexedDBSupported()) {
      reject(new Error('IndexedDB is not supported in this browser'))
      return
    }

    try {
      const request = window.indexedDB.open(DB_NAME, DB_VERSION)

      request.onerror = (event) => {
        const error = (event.target as IDBRequest).error
        console.warn('Failed to open IndexedDB:', error?.message || 'Unknown error')

        // If it's a known Safari quirk (UnknownError or Connection lost), and we have retries
        if (
          retries > 0 &&
          (error?.name === 'UnknownError' ||
            error?.message?.includes('Connection to Indexed Database server lost'))
        ) {
          console.log('Retrying IndexedDB connection...')
          setTimeout(() => {
            openDB(retries - 1)
              .then(resolve)
              .catch(reject)
          }, 100)
          return
        }

        reject(new Error(`Failed to open IndexedDB: ${error?.name || 'UnknownError'}`))
      }

      request.onsuccess = () => {
        const db = request.result

        // Handle the case where the database connection is closed by another tab or process
        db.onversionchange = () => {
          db.close()
          console.log('IndexedDB connection closed due to version change')
        }

        // Handle unexpected connection loss
        db.onabort = () => {
          db.close()
        }

        db.onerror = (event) => {
          console.error('IndexedDB database error:', event)
        }

        resolve(db)
      }

      request.onupgradeneeded = () => {
        const db = request.result
        // Delete old store if it exists (for version upgrades)
        if (db.objectStoreNames.contains(STATS_STORE_NAME)) {
          db.deleteObjectStore(STATS_STORE_NAME)
        }

        // Create object store for stats backups using date string as key
        const store = db.createObjectStore(STATS_STORE_NAME, {
          keyPath: 'id',
        })

        // Create an index on timestamp for faster retrieval
        store.createIndex('timestamp', 'timestamp', { unique: false })
      }
    } catch (e) {
      reject(e)
    }
  })
}

/**
 * Fallback localStorage implementation for browsers without IndexedDB
 */
const LocalStorageBackup = {
  async backupStats(stats: DailyStatsType): Promise<void> {
    try {
      if (!stats || Object.keys(stats).length === 0) return

      // Get existing backups
      const backups: StatsBackupEntry[] = lockr.get(LS_BACKUP_KEY) || []
      const todayKey = dateUtils.getTodayKey()

      // Find if there's already a backup for today
      const todayBackupIndex = backups.findIndex((backup) => {
        const backupDate = new Date(backup.timestamp)
        const backupKey = `${backupDate.getFullYear()}-${String(
          backupDate.getMonth() + 1
        ).padStart(2, '0')}-${String(backupDate.getDate()).padStart(2, '0')}`
        return backupKey === todayKey
      })

      if (todayBackupIndex >= 0) {
        // Update existing backup for today
        backups[todayBackupIndex] = {
          id: todayKey,
          timestamp: Date.now(),
          stats: dateUtils.deepCopy(stats),
        }
        console.log('Updated existing stats backup in localStorage')
      } else {
        // Add new backup
        backups.unshift({
          id: todayKey,
          timestamp: Date.now(),
          stats: dateUtils.deepCopy(stats),
        })
        console.log(
          `Created new stats backup with ${
            Object.keys(stats).length
          } records in localStorage`
        )
      }

      // Sort backups by timestamp (newest first)
      backups.sort((a, b) => b.timestamp - a.timestamp)

      // Limit to MAX_BACKUPS
      const limitedBackups = backups.slice(0, MAX_BACKUPS)

      // Save back to localStorage
      lockr.set(LS_BACKUP_KEY, limitedBackups)
    } catch (error) {
      console.error('Failed to backup stats to localStorage:', error)
    }
  },

  async getLatestBackup(): Promise<DailyStatsType | null> {
    try {
      const backups: StatsBackupEntry[] = lockr.get(LS_BACKUP_KEY) || []
      if (backups.length === 0) return null
      return backups[0].stats
    } catch (error) {
      console.error('Failed to get latest backup from localStorage:', error)
      return null
    }
  },

  async getAllBackups(): Promise<StatsBackupEntry[]> {
    try {
      return lockr.get(LS_BACKUP_KEY) || []
    } catch (error) {
      console.error('Failed to get all backups from localStorage:', error)
      return []
    }
  },

  async restoreFromBackup(index = 0): Promise<DailyStatsType | null> {
    try {
      const backups: StatsBackupEntry[] = lockr.get(LS_BACKUP_KEY) || []
      if (backups.length === 0 || index >= backups.length) return null
      return backups[index].stats
    } catch (error) {
      console.error('Failed to restore from backup in localStorage:', error)
      return null
    }
  },
}

/**
 * Creates a backup of stats in IndexedDB
 */
export const backupStats = async (stats: DailyStatsType): Promise<void> => {
  // Fallback to localStorage if IndexedDB is not supported
  if (!isIndexedDBSupported()) {
    return LocalStorageBackup.backupStats(stats)
  }

  if (!stats || Object.keys(stats).length === 0) {
    console.log('Not backing up empty stats')
    return
  }

  try {
    const db = await openDB()
    const todayKey = dateUtils.getTodayKey()

    return new Promise<void>((resolve, reject) => {
      try {
        const tx = db.transaction(STATS_STORE_NAME, 'readwrite')
        const store = tx.objectStore(STATS_STORE_NAME)

        // Handle errors and completion
        tx.onerror = () => {
          const error = tx.error
          console.warn('IndexedDB transaction error:', error?.message || 'Unknown error')
          db.close()

          // If transaction fails due to connection loss, we fallback but resolve (don't throw fatal)
          if (error?.message?.includes('Connection to Indexed Database server lost')) {
            LocalStorageBackup.backupStats(stats).then(resolve).catch(reject)
            return
          }

          reject(new Error('Failed to backup stats: Transaction error'))
        }

        tx.oncomplete = () => {
          console.log('Backup transaction completed successfully')
          db.close()
          resolve()
        }

        // Try to get today's backup first
        const getRequest = store.get(todayKey)

        getRequest.onsuccess = () => {
          const todayBackup = getRequest.result

          if (todayBackup) {
            // Update existing backup for today
            todayBackup.timestamp = Date.now()
            todayBackup.stats = dateUtils.deepCopy(stats)

            store.put(todayBackup)
          } else {
            // Create a new backup for today
            const newBackup: StatsBackupEntry = {
              id: todayKey,
              timestamp: Date.now(),
              stats: dateUtils.deepCopy(stats),
            }

            store.add(newBackup)
          }

          // Maintain maximum MAX_BACKUPS backups
          const countRequest = store.count()
          countRequest.onsuccess = () => {
            if (countRequest.result <= MAX_BACKUPS) return

            // Get all backups and sort by timestamp
            const getAllRequest = store.getAll()
            getAllRequest.onsuccess = () => {
              const allBackups: StatsBackupEntry[] = getAllRequest.result

              if (allBackups.length <= MAX_BACKUPS) return

              // Sort by timestamp (newest first)
              allBackups.sort((a, b) => b.timestamp - a.timestamp)

              // Delete the oldest backups beyond our limit
              const backupsToDelete = allBackups.slice(MAX_BACKUPS)
              backupsToDelete.forEach((backup) => {
                store.delete(backup.id)
              })

              console.log(`Deleted ${backupsToDelete.length} old backups`)
            }
          }
        }
      } catch (e: any) {
        db.close()
        // If transaction creation fails (e.g. database connection lost right after opening)
        if (e?.message?.includes('Connection to Indexed Database server lost')) {
          LocalStorageBackup.backupStats(stats).then(resolve).catch(reject)
        } else {
          reject(e)
        }
      }
    })
  } catch (error: any) {
    if (
      error?.message?.includes('Connection to Indexed Database server lost') ||
      error?.message?.includes('Failed to open IndexedDB')
    ) {
      console.warn('Falling back to localStorage due to IndexedDB issue:', error.message)
      return LocalStorageBackup.backupStats(stats)
    }
    console.error('Failed to backup stats to IndexedDB:', error)
    // Fallback to localStorage if IndexedDB operations fail
    return LocalStorageBackup.backupStats(stats)
  }
}

/**
 * Gets the most recent backup
 */
export const getLatestBackup = async (): Promise<DailyStatsType | null> => {
  // Fallback to localStorage if IndexedDB is not supported
  if (!isIndexedDBSupported()) {
    return LocalStorageBackup.getLatestBackup()
  }

  try {
    const db = await openDB()

    return new Promise<DailyStatsType | null>((resolve, reject) => {
      try {
        const tx = db.transaction(STATS_STORE_NAME, 'readonly')
        const store = tx.objectStore(STATS_STORE_NAME)

        // Get all backups and find the newest one
        const getAllRequest = store.getAll()

        getAllRequest.onsuccess = () => {
          const backups: StatsBackupEntry[] = getAllRequest.result

          if (backups.length === 0) {
            resolve(null)
          } else {
            // Sort by timestamp (newest first)
            backups.sort((a, b) => b.timestamp - a.timestamp)
            resolve(backups[0].stats)
          }
        }

        getAllRequest.onerror = () => {
          console.warn('Failed to get backups from IndexedDB')
          db.close()
          // Fallback on error
          LocalStorageBackup.getLatestBackup().then(resolve).catch(reject)
        }

        tx.oncomplete = () => {
          db.close()
        }

        tx.onerror = () => {
          db.close()
          LocalStorageBackup.getLatestBackup().then(resolve).catch(reject)
        }
      } catch (e: any) {
        db.close()
        if (e?.message?.includes('Connection to Indexed Database server lost')) {
          LocalStorageBackup.getLatestBackup().then(resolve).catch(reject)
        } else {
          reject(e)
        }
      }
    })
  } catch (error: any) {
    if (
      error?.message?.includes('Connection to Indexed Database server lost') ||
      error?.message?.includes('Failed to open IndexedDB')
    ) {
      return LocalStorageBackup.getLatestBackup()
    }
    console.error('Failed to get latest backup from IndexedDB:', error)
    return LocalStorageBackup.getLatestBackup()
  }
}

/**
 * Gets all available backups
 */
export const getAllBackups = async (): Promise<StatsBackupEntry[]> => {
  // Fallback to localStorage if IndexedDB is not supported
  if (!isIndexedDBSupported()) {
    return LocalStorageBackup.getAllBackups()
  }

  try {
    const db = await openDB()

    return new Promise<StatsBackupEntry[]>((resolve, reject) => {
      try {
        const tx = db.transaction(STATS_STORE_NAME, 'readonly')
        const store = tx.objectStore(STATS_STORE_NAME)

        const getAllRequest = store.getAll()

        getAllRequest.onsuccess = () => {
          const backups: StatsBackupEntry[] = getAllRequest.result

          // Sort by timestamp (newest first)
          backups.sort((a, b) => b.timestamp - a.timestamp)
          resolve(backups)
        }

        getAllRequest.onerror = () => {
          console.warn('Failed to get all backups from IndexedDB')
          db.close()
          LocalStorageBackup.getAllBackups().then(resolve).catch(reject)
        }

        tx.oncomplete = () => {
          db.close()
        }

        tx.onerror = () => {
          db.close()
          LocalStorageBackup.getAllBackups().then(resolve).catch(reject)
        }
      } catch (e: any) {
        db.close()
        if (e?.message?.includes('Connection to Indexed Database server lost')) {
          LocalStorageBackup.getAllBackups().then(resolve).catch(reject)
        } else {
          reject(e)
        }
      }
    })
  } catch (error: any) {
    if (
      error?.message?.includes('Connection to Indexed Database server lost') ||
      error?.message?.includes('Failed to open IndexedDB')
    ) {
      return LocalStorageBackup.getAllBackups()
    }
    console.error('Failed to get all backups from IndexedDB:', error)
    return LocalStorageBackup.getAllBackups()
  }
}

/**
 * Restore from a specific backup by index (0 = latest)
 */
export const restoreFromBackup = async (index = 0): Promise<DailyStatsType | null> => {
  // Fallback to localStorage if IndexedDB is not supported
  if (!isIndexedDBSupported()) {
    return LocalStorageBackup.restoreFromBackup(index)
  }

  try {
    const backups = await getAllBackups()

    if (backups.length === 0 || index >= backups.length) {
      return null
    }

    return backups[index].stats
  } catch (error) {
    console.error('Failed to restore from backup:', error)
    return null
  }
}

/**
 * Check if the current stats look like they might have been reset
 * compared to the most recent backup
 */
export const detectStatsReset = async (
  currentStats: DailyStatsType | undefined
): Promise<boolean> => {
  if (!currentStats || Object.keys(currentStats).length === 0) {
    const latestBackup = await getLatestBackup()
    // If we have no stats but we do have a backup with stats, likely a reset
    return latestBackup !== null && Object.keys(latestBackup).length > 0
  }

  const latestBackup = await getLatestBackup()
  if (!latestBackup) return false

  // If current stats have significantly fewer entries than the backup
  // (allowing for some natural removal of old stats)
  const currentKeysCount = Object.keys(currentStats).length
  const backupKeysCount = Object.keys(latestBackup).length

  return currentKeysCount < backupKeysCount * 0.5 // If we lost more than 50% of our stats
}

/**
 * Export formatBackupDate for use in other components
 */
export const formatBackupDate = dateUtils.formatBackupDate
